import { useState } from "react";
import { Header } from "@/components/layout/Header";
import { HeroSection } from "@/components/sections/HeroSection";
import { ComplianceCard } from "@/components/compliance/ComplianceCard";
import { Button } from "@/components/ui/button";
import { ArrowRight, Shield, FileText, GitBranch, Building2, Cpu, Users } from "lucide-react";
import { BetaSignupDialog } from "@/components/beta/BetaSignupDialog";

const Index = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const sampleNetworks = [
    {
      title: "Site: HQ Stockholm",
      description: "Completed task",
      status: "success" as const,
      badgeLabel: "Task Done",
      lastUpdated: "2025-11-25",
      icon: Building2,
    },
    {
      title: "PLC: Production Line 02",
      description: "Vulnerability detected",
      status: "warning" as const,
      badgeLabel: "Medium",
      lastUpdated: "2025-11-24",
      icon: Cpu,
    },
    {
      title: "Supplier: ACME Security",
      description: "Requires attention",
      status: "danger" as const,
      badgeLabel: "High",
      lastUpdated: "2025-11-20",
      icon: Users,
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <BetaSignupDialog open={dialogOpen} onOpenChange={setDialogOpen} />
      <Header />
      <HeroSection />

      {/* Beta Tester Callout - Playful IT/OT Angle */}
      <section className="py-12 bg-accent/5 border-y border-accent/20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">OT Engineers & IT Teams: We Need Your Input</h2>
            <p className="text-lg text-foreground/90 mb-6">
              We're building ComplyManager <strong>with</strong> you, not <strong>for</strong> you. If you've ever spent
              3 hours on a firewall rule request or lost a network diagram in someone's email, you know the pain we're
              solving.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="p-4 bg-card rounded-lg border border-accent/20">
                <h4 className="font-semibold mb-2">✓ Free Beta Access</h4>
                <p className="text-sm text-foreground/85">
                  No credit card. No commitment. Just test it and tell us what sucks.
                </p>
              </div>
              <div className="p-4 bg-card rounded-lg border border-accent/20">
                <h4 className="font-semibold mb-2">✓ Shape the Product</h4>
                <p className="text-sm text-foreground/85">Direct line to the dev team. Your feedback = our roadmap.</p>
              </div>
              <div className="p-4 bg-card rounded-lg border border-accent/20">
                <h4 className="font-semibold mb-2">✓ Early Adopter Perks</h4>
                <p className="text-sm text-foreground/85">Locked-in pricing when we launch. Plus bragging rights.</p>
              </div>
            </div>

            <p className="text-lg font-semibold text-foreground">
              Interested?{" "}
              <button onClick={() => setDialogOpen(true)} className="underline hover:opacity-80 transition-opacity">
                Join the beta program →
              </button>
            </p>
            <p className="text-sm text-foreground/80 mt-4">
              Rolling admissions • We'll onboard you as soon as possible!
            </p>
          </div>
        </div>
      </section>

      {/* Dashboard Preview Section */}
      <section id="how-it-works" className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">One place where everyone "SaaS" the same network.</h2>
            <p className="text-xl text-foreground/90 max-w-2xl mx-auto">
              No more "What is this system connected to?" or "Who supports or is responsible for this system?"
              Everything in one place, always up to date.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {sampleNetworks.map((item, index) => (
              <ComplianceCard key={index} {...item} />
            ))}
          </div>

          <div className="text-center">
            <Button variant="hero" size="lg">
              Explore Network View
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 bg-muted/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Built to End the IT vs. OT Battle</h2>
            <p className="text-xl text-foreground/90 max-w-2xl mx-auto">
              Made by people who understand both worlds. Because system documentation and asset relationships shouldn't
              be a source of conflict.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-card rounded-lg shadow-card border border-border">
              <FileText className="h-12 w-12 text-foreground/90 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-3">System Documentation That Actually Works</h3>
              <p className="text-foreground/85">
                Asset relationships, system layouts, and connections in one place. Version controlled, always current,
                and actually useful.
              </p>
            </div>
            <div className="text-center p-6 bg-card rounded-lg shadow-card border border-border">
              <GitBranch className="h-12 w-12 text-foreground/90 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-3">Track System Changes Without the Email Hell</h3>
              <p className="text-foreground/85">
                Submit system updates, track modifications, manage ownership transfers – without pinging IT every 3
                hours.
              </p>
            </div>
            <div className="text-center p-6 bg-card rounded-lg shadow-card border border-border">
              <Shield className="h-12 w-12 text-foreground/90 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-3">Who Owns What? Now You Know.</h3>
              <p className="text-foreground/85">
                See which systems connect where, who's responsible for what, and why. No more mystery configurations or
                lost ownership.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-primary">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">Ready to Get Started?</h2>
          <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto text-white">
            Schedule a demo to see ComplyManager in action.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="hero"
              size="lg"
              className="bg-white text-black hover:bg-gray-300"
              onClick={() => setDialogOpen(true)}
            >
              Schedule a Demo
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-card border-t border-border">
        <div className="container mx-auto px-4 text-center">
          <p className="text-foreground/85">
            &copy; 2025{" "}
            <a
              href="https://www.autelion.com"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline font-medium text-foreground/95"
            >
              Autelion AB
            </a>
            . All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Index;
